import { useRef } from 'react';
import styles from './secondScreen.module.css';
import NewBox from '../../components/box/box';
import Can from '../../components/can/Can';
import shieldIcon from '../../icons/shield.json';
import globeIcon from '../../icons/globe.json';
import moneybagIcon from '../../icons/moneybag.json';
import useScrollReveal from '../../hooks/useScrollReveal';

const quotes = [
  '"תעשי קורס ותוך חודשיים היומן מפוצץ"',
  '"תוך חודשיים ואת מרוויחה יותר מהייטקיסט"',
];

const boxes = [
  {
    icon: shieldIcon,
    title: 'לא חיכו שהעבודה תגיע לבד',
    desc: 'מי שבאמת רוצה להצליח יוזמת! דואגת לפרסם, לתרגל, לצלם ולשווק את עצמה ולהשיג את הלקוחות שלה  -  ולא מחכה שלקוחות יגיעו כמו קסם.',
  },
  {
    icon: globeIcon,
    title: 'תרגלו והתמידו כל יום חדש',
    desc: 'מי שרוצה להפוך להיות מומחית בתחום חייבת לתרגל! טכניקה לא נבנית ביום ולא ביומיים  -  היא נבנית בעבודה ותרגול מתמשכים.',
  },
  {
    icon: moneybagIcon,
    title: 'לא ויתרו כשהיה קשה',
    desc: 'הבנות שאת רואה שמצליחות בתחום? גם להן היו ימים קשים, ימים בלי לקוחות וימים שהדברים לא הסתדרו כמו שהן רצו  -  אבל אף אחת מהן לא ויתרה על החלום שלה!',
  },
];

function SecondScreen() {
  const sectionRef = useRef(null);
  useScrollReveal(sectionRef);

  return (
    <section className={styles.dream} ref={sectionRef}>
      {/* ── Ambient Glows ── */}
      <div className={styles.glowTop} />
      <div className={styles.glowBottom} />
      <div className={styles.glowCenter} />

      {/* ── Sparkles ── */}
      {[...Array(10)].map((_, i) => (
        <div key={i} className={`${styles.sparkle} ${styles[`sp${i + 1}`]}`} />
      ))}

      {/* ── Decorative arcs ── */}
      <div className={styles.arcLeft} />
      <div className={styles.arcRight} />

      <div className={styles.container}>

        {/* ════ DREAM LIFE ════ */}
        <div className={styles.sectionLabel} data-sr="0">
          <span className={styles.labelLine} />
          <span className={styles.labelText}></span>
          <span className={styles.labelLine} />
        </div>

        <h2 className={styles.mainTitle} data-sr="0.08">
          תדמייני לעצמך את{' '}
          <em className={styles.titleHighlight}>החיים האלה:</em>
        </h2>

        <div className={styles.canGrid} data-sr="0.14">
          <Can
            text="אין לך אף בוס על הראש  -  את בוחרת מתי לעבוד וכמה לעבוד."
            icon={globeIcon}
          />
          <Can
            text="את מרוויחה הרבה יותר משכר מינימום בהרבה פחות זמן עבודה."
            icon={moneybagIcon}
          />
          <Can
            text="את קמה בבוקר  -  אבל הפעם את קמה לעבוד בתחום שתמיד אהבת."
            icon={shieldIcon}
          />
        </div>

        <p className={styles.catchPhrase} data-sr="0.18">
          זו חד משמעית יכולה להיות המציאות שלך
        </p>

        {/* ════ REALITY CHECK ════ */}
        <div className={styles.dividerRow} data-sr="0.06">
          <div className={styles.dividerLine} />
          <div className={styles.dividerLine} />
        </div>

        <h2 className={styles.mainTitle} style={{ fontSize: 'clamp(26px, 3.5vw, 38px)' }} data-sr="0.1">
          אבל <em className={styles.titleHighlight}>לא כל כך מהר..</em>
        </h2>

        <p className={styles.sectionIntro} data-sr="0.12">בטח שמעת את המשפטים הבאים לא פעם ולא פעמיים</p>

        <div className={styles.quotesGrid}>
          {quotes.map((q, i) => (
            <div key={i} className={styles.quoteRibbon} data-sr={String(0.16 + i * 0.18)} data-sr-y="0">
              <div className={styles.quoteRibbonShine} />
              <p className={styles.quoteRibbonText}>{q}</p>
            </div>
          ))}
        </div>

        <p className={styles.centerP} data-sr="0.1">
          נשמע מדהים, לא? רק שבמציאות{' '}
          <em className={styles.discEm}>רוב הבנות שקנו את החלום הזה נשארו עם תעודה במגירה ובלי לקוחה אחת.</em>
        </p>

        {/* ════ SUCCESS BOXES ════ */}
        <p className={styles.successLead} data-sr="0.08">הבנות שבאמת הצליחו הן אלה ש:</p>

        <div className={styles.boxesGrid}>
          {boxes.map((b, i) => (
            <div key={i} data-sr={String(0.06 + i * 0.07)}>
              <NewBox
                icon={b.icon}
                title={b.title}
                description={b.desc}
              />
            </div>
          ))}
        </div>

        {/* ════ CLOSING ════ */}
        <div className={styles.closingPanel} data-sr="0.1">
          <div className={styles.closingGlow} />
          <h2 className={styles.closingTitle}>
          <em className={styles.titleHighlight}>והכי חשוב.</em>
            </h2>
            <br />
          <p className={styles.closingUnderlined}>
            הייתה להן מישהי בצד שלא נעלמה ברגע שהקורס נגמר.
          </p>
          <p className={styles.closingSmall}>
            אז אם את באמת רצינית לגבי החלום שלך חשוב לי שתדעי
          </p>
          <h2 className={styles.closingTitle}>
            <em className={styles.titleHighlight}>את במקום הנכון.</em>
          </h2>
        </div>

      </div>
    </section>
  );
}

export default SecondScreen;
